<script setup lang="ts">
  import { storeToRefs } from "pinia";
  import { ExternalLink } from "lucide-vue-next";
  import { IndexTable, IndexTableName, IndexTableDate, IndexTableEmpty } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { ActionsDropdown } from "@/components/episto-ui/actions-dropdown";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Button } from "@/components/ui/button";
  import { usePricerProjectsStore } from "@/stores/pricer/pricerProjects";
  import { usePricerProjectCard } from "@/utils/composables/pricerProjectCard";
  import { useRowNavigation } from "@/composables/useRowNavigation";
  import type { PricerProject } from "@/types";

  const pricerProjectsStore = usePricerProjectsStore();
  const { projects, projectsCount, currentPage } = storeToRefs(pricerProjectsStore);
  const { changePage } = pricerProjectsStore;
  const { determineRoundedValue, determineOwner, pricerDropdownActions } = usePricerProjectCard();
  const { openRow } = useRowNavigation();

  const columns: DataTableColumn<PricerProject>[] = [
    { key: "name", label: "Project" },
    { key: "client", label: "Client", width: 180 },
    { key: "n", label: "N", width: 80 },
    { key: "cpi", label: "CPI", width: 90 },
    { key: "amount", label: "Amount", width: 110 },
    { key: "owner", label: "Owner", width: 130 },
    { key: "date", label: "Date", width: 90 },
  ];

  const hubspotUrl = (project: PricerProject) => `https://app.hubspot.com/contacts/9187193/record/0-3/${project.crm_id}`;

  const editRoute = (project: PricerProject) => ({ name: "PricerProjectEdit", params: { token: project.token } });
</script>

<template>
  <IndexTable
    :items="projects"
    :columns="columns"
    :current-page="currentPage"
    :total="projectsCount"
    actions-class="w-[92px]"
    item-key="token"
    empty-message="No project yet."
    @row-click="(project, event) => openRow(editRoute(project), event)"
    @page-change="changePage">
    <template #cell-name="{ item }">
      <IndexTableName :name="item.name" />
    </template>
    <template #cell-client="{ item }">
      <Tooltip
        :content="item.end_client"
        :disabled="!item.end_client || item.end_client === item.client">
        <span class="block truncate">{{ item.client }}</span>
      </Tooltip>
    </template>
    <template #cell-n="{ item }">
      <span class="tabular-nums">{{ item.n }}</span>
    </template>
    <template #cell-amount="{ item }">
      <span class="font-semibold tabular-nums text-primary">€ {{ determineRoundedValue(item.total_price) }}</span>
    </template>
    <template #cell-cpi="{ item }">
      <span
        v-if="item.n > 0"
        class="tabular-nums text-foreground-secondary">
        € {{ determineRoundedValue(item.total_recruitment / item.n, "cpi") }}
      </span>
      <IndexTableEmpty v-else />
    </template>
    <template #cell-owner="{ item }">
      <span class="block truncate">{{ determineOwner(item.owner_id) }}</span>
    </template>
    <template #cell-date="{ item }">
      <IndexTableDate :date="item.created_at" />
    </template>
    <template #actions="{ item }">
      <div
        class="flex items-center justify-end gap-1"
        @click.stop>
        <Tooltip
          v-if="item.crm_id"
          content="Open in HubSpot">
          <Button
            as="a"
            :href="hubspotUrl(item)"
            target="_blank"
            rel="noopener"
            variant="ghost"
            size="icon-sm"
            aria-label="Open in HubSpot">
            <ExternalLink />
          </Button>
        </Tooltip>
        <span
          v-else
          class="size-7" />
        <ActionsDropdown
          :actions="pricerDropdownActions"
          :token="item.token" />
      </div>
    </template>
  </IndexTable>
</template>
